import React, { useEffect } from 'react';
import Icon from '../AppIcon';

const UploadProgress = ({ 
  progress = 0, 
  status = 'uploading', 
  filename = '', 
  onCancel,
  onRetry 
}) => {
  const getStatusConfig = () => {
    switch (status) {
      case 'uploading':
        return {
          icon: 'Upload',
          iconColor: 'var(--color-secondary)',
          text: 'Uploading contract...',
          showProgress: true,
          bgColor: 'bg-secondary/10',
          borderColor: 'border-secondary/20'
        };
      case 'processing':
        return {
          icon: 'FileSearch',
          iconColor: 'var(--color-accent)',
          text: 'Analyzing contract data...',
          showProgress: true,
          bgColor: 'bg-accent/10',
          borderColor: 'border-accent/20'
        };
      case 'complete':
        return {
          icon: 'CheckCircle',
          iconColor: 'var(--color-success)',
          text: 'Contract processed successfully',
          showProgress: false,
          bgColor: 'bg-success/10',
          borderColor: 'border-success/20'
        };
      case 'error':
        return {
          icon: 'AlertCircle',
          iconColor: 'var(--color-error)',
          text: 'Processing failed',
          showProgress: false,
          bgColor: 'bg-error/10',
          borderColor: 'border-error/20'
        };
      default:
        return {
          icon: 'Upload',
          iconColor: 'var(--color-secondary)',
          text: 'Preparing upload...',
          showProgress: false,
          bgColor: 'bg-muted',
          borderColor: 'border-border'
        };
    }
  };

  const config = getStatusConfig();

  // Auto-dismiss after 3 seconds for completed uploads
  useEffect(() => {
    if (status === 'complete' && progress >= 100) {
      const timer = setTimeout(() => {
        // Component will be unmounted by parent
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [status, progress]);

  return (
    <div className={`
      fixed top-20 left-0 right-0 z-progress
      ${config.bgColor} ${config.borderColor} border-b
      transition-all duration-200 ease-smooth
    `}>
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Status Icon */}
            <div className={`
              w-10 h-10 rounded-lg flex items-center justify-center
              ${status === 'processing' ? 'animate-pulse' : ''}
            `}>
              <Icon 
                name={config.icon} 
                size={20} 
                color={config.iconColor}
                strokeWidth={2}
              />
            </div>

            {/* Status Text and Filename */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <span className="font-body font-medium text-foreground">
                  {config.text}
                </span>
                {status === 'processing' && (
                  <div className="flex space-x-1">
                    <div className="w-1 h-1 bg-accent rounded-full animate-pulse"></div>
                    <div className="w-1 h-1 bg-accent rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-1 h-1 bg-accent rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                )}
              </div>
              {filename && (
                <div className="text-sm text-muted-foreground font-mono truncate mt-1">
                  {filename}
                </div>
              )}
            </div>

            {/* Progress Percentage */}
            {config.showProgress && (
              <div className="text-sm font-mono font-medium text-foreground">
                {Math.round(progress)}%
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2 ml-4">
            {status === 'error' && onRetry && (
              <button
                onClick={onRetry}
                className="
                  flex items-center space-x-2 px-3 py-1.5 rounded-md
                  bg-error/10 hover:bg-error/20 border border-error/20
                  text-error font-body font-medium text-sm
                  transition-colors duration-200 ease-smooth
                "
              >
                <Icon name="RotateCcw" size={14} color="var(--color-error)" />
                <span>Retry</span>
              </button>
            )}
            
            {(status === 'uploading' || status === 'processing') && onCancel && (
              <button
                onClick={onCancel}
                className="
                  flex items-center space-x-2 px-3 py-1.5 rounded-md
                  bg-muted hover:bg-muted-foreground/10 border border-border
                  text-muted-foreground hover:text-foreground font-body font-medium text-sm
                  transition-colors duration-200 ease-smooth
                "
              >
                <Icon name="X" size={14} color="currentColor" />
                <span>Cancel</span>
              </button>
            )}

            {status === 'complete' && (
              <button
                onClick={() => {/* Parent handles dismissal */}}
                className="
                  p-1.5 rounded-md hover:bg-success/20
                  text-success transition-colors duration-200 ease-smooth
                "
                aria-label="Dismiss notification"
              >
                <Icon name="X" size={16} color="var(--color-success)" />
              </button>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        {config.showProgress && (
          <div className="mt-3">
            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
              <div 
                className={`
                  h-full transition-all duration-300 ease-smooth rounded-full
                  ${status === 'uploading' ? 'bg-secondary' : 'bg-accent'}
                  ${status === 'processing' ? 'animate-pulse-subtle' : ''}
                `}
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadProgress;